/* eslint-disable no-restricted-imports */
import {doAsync} from '@aboutyou/utils';
import type {QueryClient} from '@tanstack/react-query';
import {
  MutationFunction,
  QueryFunction,
  QueryKey,
  QueryObserver,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
  hashKey,
  keepPreviousData,
  useMutation as useMutationOriginal,
  useQueryClient as useQueryClientOriginal,
  useQuery as useQueryOriginal,
  useSuspenseQuery as useSuspenseQueryOriginal,
} from '@tanstack/react-query';
import {startTransition, useCallback, useMemo} from 'react';
import {isValidationError} from './throwable';
import {useUpdateQueryCache} from './useUpdateQueryCache';

export type {QueryObserverResult, UseQueryOptions} from '@tanstack/react-query';

export type UseQueryResultWithUpdateCache<TData, TError> = UseQueryResult<
  TData,
  TError
> & {
  updateCache: (state: TData | ((oldData: TData | undefined) => TData)) => void;
};

export const defaultRetry = (failureCount: number, error: unknown) => {
  if (isValidationError(error)) {
    return false;
  }
  return failureCount <= 0;
};

const TWENTY_MINUTES_IN_MS = 1000 * 60 * 20;
export const DEFAULT_QUERY_CONFIG = {
  placeholderData: keepPreviousData,
  retry: defaultRetry,
  retryDelay: 1500,
  staleTime: TWENTY_MINUTES_IN_MS,
  gcTime:
    process.env.NODE_ENV === 'test'
      ? // 5 seconds for tests makes the test to quit after 5 seconds, otherwise the test hangs until the cache expires
        // https://github.com/TanStack/query/issues/1847
        5000
      : TWENTY_MINUTES_IN_MS,
  refetchOnWindowFocus: false,
};

export type TUseQueryConfig<TQueryFnData, TError, TData> = Omit<
  UseQueryOptions<TQueryFnData, TError, TData>,
  'queryKey'
> & {
  onSuccess?: (data: TData) => void;
  onError?: (error: TError) => void;
  keepPreviousData?: boolean;
};

export const useSuspenseQuery = <TQueryFnData = unknown>(
  queryKey: string,
  queryFunction: QueryFunction<TQueryFnData>,
) => {
  const query = useSuspenseQueryOriginal<TQueryFnData>({
    queryKey: [queryKey],
    queryFn: queryFunction,
  });

  return query;
};

export const useQuery = <
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
>(
  queryKey: string,
  queryFunction: QueryFunction<TQueryFnData>,
  config?: TUseQueryConfig<TQueryFnData, TError, TData>,
): UseQueryResultWithUpdateCache<TData, TError> => {
  if (!process.browser) {
    return {
      data: undefined,
      error: null,
      isError: false,
      isIdle: false,
      isLoading: true,
      isLoadingError: false,
      isRefetchError: false,
      isSuccess: false,
      status: 'loading',
      updateCache: updateCacheServer,
    } as unknown as UseQueryResultWithUpdateCache<TData, TError>;
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const {updateCache, queryClient} = useUpdateQueryCache<TData>({
    key: queryKey,
  });

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const query = useQueryOriginal({
    queryKey: [queryKey],
    queryFn: async params => {
      /**
       * This is a custom implementation to call the `onSuccess` callback when the data is set in the cache
       * and to call the `onError` callback when an error occurs.
       *
       * Its important to call the `onSuccess` or `onError` callback for all observers of the query key.
       * This callback is only fired on the client side.
       */
      try {
        const data = await queryFunction(params);

        if (process.browser && data) {
          doAsync(() => {
            startTransition(() => {
              const observers = queryClient.getQueryObservers(queryKey);

              for (const observer of observers) {
                if (hasOnSuccessCallback(observer.options)) {
                  observer.options.onSuccess(data);
                }
              }
            });
          });
        }

        return data;
      } catch (error) {
        if (process.browser) {
          const observers = queryClient.getQueryObservers(queryKey);

          for (const observer of observers) {
            if (hasOnErrorCallback(observer.options)) {
              observer.options.onError(error);
            }
          }
        }

        throw error;
      }
    },
    ...DEFAULT_QUERY_CONFIG,
    ...config,
    ...(config?.keepPreviousData === false
      ? {placeholderData: undefined}
      : {placeholderData: DEFAULT_QUERY_CONFIG.placeholderData}),
  }) as UseQueryResultWithUpdateCache<TData, TError>;

  query.updateCache = updateCache;

  return query;
};

const hasOnSuccessCallback = <TData, TError>(
  config: TUseQueryConfig<TData, TError, TData>,
): config is TUseQueryConfig<TData, TError, TData> & {
  onSuccess: VoidFunction;
} => !!config.onSuccess;

const hasOnErrorCallback = <TData, TError>(
  config: TUseQueryConfig<TData, TError, TData>,
): config is TUseQueryConfig<TData, TError, TData> & {onError: VoidFunction} =>
  !!config.onError;

interface ReducedUseMutationResult<TData, TError, TVariables, TContext> {
  mutateAsync: UseMutationResult<
    TData,
    TError,
    TVariables,
    TContext
  >['mutateAsync'];
  isPending: UseMutationResult<
    TData,
    TError,
    TVariables,
    TContext
  >['isPending'];
  isSuccess: UseMutationResult<
    TData,
    TError,
    TVariables,
    TContext
  >['isSuccess'];
  isError: UseMutationResult<TData, TError, TVariables, TContext>['isError'];
  reset: UseMutationResult<TData, TError, TVariables, TContext>['reset'];
}

export const useMutation = <
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown,
>(
  mutationFunction: MutationFunction<TData, TVariables>,
  config?: UseMutationOptions<TData, TError, TVariables, TContext>,
): ReducedUseMutationResult<TData, TError, TVariables, TContext> => {
  if (!process.browser) {
    return {
      mutateAsync: () => {
        throw new Error(
          'Data fetcher query mutations are not supported on the server',
        );
      },
      isPending: false,
      isSuccess: false,
      isError: false,
      reset: () => null,
    };
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useMutationOriginal({
    mutationFn: mutationFunction,
    ...config,
  });
};

export interface ReducedQueryClient {
  removeQuery: (key: string) => void;
  fetchQuery: <T>(
    key: string,
    queryFn: () => Promise<T>,
    config?: Omit<
      Parameters<QueryClient['fetchQuery']>[0],
      'queryKey' | 'queryFn'
    >,
  ) => Promise<T>;
  setQueryData: <T>(
    key: string,
    data: T | ((oldData: T | undefined) => T),
  ) => T | undefined;
  setQueryDefaults: (
    key: string,
    data: Parameters<QueryClient['setQueryDefaults']>[1],
  ) => ReturnType<QueryClient['setQueryDefaults']>;
  getQueryData: <T>(key: string) => T | undefined;
  getQueriesData: (methodName: string) => Array<[QueryKey, unknown]>;
  getQueryObservers: <T>(
    key: string,
  ) => Array<QueryObserver<unknown, T, unknown, unknown, QueryKey>>;
  invalidateQueries: QueryClient['invalidateQueries'];
  getQueryCache: QueryClient['getQueryCache'];
  getMutationCache: QueryClient['getMutationCache'];
}

export const useQueryClient = (): ReducedQueryClient => {
  if (!process.browser) {
    return {
      getQueriesData: () => {
        throw new Error(
          'Data fetcher query client is not supported on the server',
        );
      },
      setQueryData: () => {
        throw new Error(
          'Data fetcher query client is not supported on the server',
        );
      },
      getQueryData: () => {
        throw new Error(
          'Data fetcher query client is not supported on the server',
        );
      },
      fetchQuery: () => {
        throw new Error(
          'Data fetcher query client is not supported on the server',
        );
      },
      removeQuery: () => {
        throw new Error(
          'Data fetcher query client is not supported on the server',
        );
      },
      setQueryDefaults: () => {
        throw new Error(
          'Data fetcher query client is not supported on the server',
        );
      },
      getQueryObservers: () => {
        throw new Error(
          'Data fetcher query client is not supported on the server',
        );
      },
      invalidateQueries: () => {
        throw new Error(
          'Data fetcher query client is not supported on the server',
        );
      },
      getQueryCache: () => {
        throw new Error(
          'Data fetcher query client is not supported on the server',
        );
      },
      getMutationCache: () => {
        throw new Error(
          'Data fetcher query client is not supported on the server',
        );
      },
    };
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const queryClient = useQueryClientOriginal();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const getQueryObservers = useCallback(
    <T>(key: string) => {
      return (
        queryClient.getQueryCache().get<T>(hashKey([key]))?.observers ?? []
      );
    },
    [queryClient],
  );

  /**
   * This is a reduced version of the query client that makes the usage easier:
   * - just pass the `key` as a string instead of an array
   * - automatically call the `onSuccess` callback when setting the query data
   * - exposes a `getQueryObservers` method to get the observers for a given query key
   */
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const reducedQueryClient = useMemo(
    (): ReducedQueryClient => ({
      getQueriesData: methodName => {
        return queryClient.getQueriesData({
          predicate: query => {
            const key = (query.queryKey[0] as string)
              .replace('"', '')
              .split(':')[0];
            return key === methodName;
          },
        });
      },
      getQueryObservers,
      removeQuery: key => {
        return queryClient.removeQueries({queryKey: [key]});
      },
      setQueryDefaults: (key, options) => {
        return queryClient.setQueryDefaults([key], options);
      },
      getQueryData: key => {
        return queryClient.getQueryData([key]);
      },
      fetchQuery: <T>(key: any, queryFn: any, config: any) => {
        return queryClient.fetchQuery({
          queryKey: [key],
          queryFn,
          ...config,
        }) as Promise<T>;
      },
      setQueryData: <T>(
        key: string,
        data: T | ((oldData: T | undefined) => T),
      ) => {
        const newData = queryClient.setQueryData<T>([key], data);

        startTransition(() => {
          const observers = getQueryObservers(key);

          for (const observer of observers) {
            if (hasOnSuccessCallback(observer.options)) {
              observer.options.onSuccess(newData);
            }
          }
        });

        return newData;
      },
      invalidateQueries: queryClient.invalidateQueries.bind(queryClient),
      getQueryCache: queryClient.getQueryCache.bind(queryClient),
      getMutationCache: queryClient.getMutationCache.bind(queryClient),
    }),
    [getQueryObservers, queryClient],
  );

  return reducedQueryClient;
};

export const updateCacheServer = () => {
  throw new Error('updateCache is not expected to be called on the server');
};
export const refetchServer = () => {
  throw new Error('refetch is not expected to be called on the server');
};
